/** Detail stage chosen when a version is uploaded. Groups appear only after a file uses them. */
export const PROJECT_FILE_DETAIL_STAGES = [
  'estimating',
  'conceptual',
  'joinery',
  'supplementary_details',
] as const;

export type ProjectFileDetailStage = (typeof PROJECT_FILE_DETAIL_STAGES)[number];

export const PROJECT_FILE_DETAIL_STAGE_LABELS: Record<ProjectFileDetailStage, string> = {
  estimating: 'Conceptual',
  conceptual: 'Engineer Review',
  joinery: 'Joinery',
  supplementary_details: 'Supplementary details',
};

export function parseProjectFileDetailStage(raw: unknown): ProjectFileDetailStage | undefined {
  return (PROJECT_FILE_DETAIL_STAGES as readonly string[]).includes(raw as string)
    ? (raw as ProjectFileDetailStage)
    : undefined;
}

export interface ProjectFileVersionStageGroup<T> {
  stage: ProjectFileDetailStage;
  label: string;
  files: T[];
  /** This group holds the newest version in the track. */
  containsNewest: boolean;
}

/** One row in the version stream, newest first. A stage sits where its newest file belongs. */
export type ProjectFileVersionStreamItem<T> =
  | { kind: 'file'; file: T }
  | { kind: 'stage'; group: ProjectFileVersionStageGroup<T> };

type VersionForStageGroup = {
  detailStage?: ProjectFileDetailStage;
  versionIndex?: number;
  uploadedAt?: Date;
};

function recency(version: VersionForStageGroup): number {
  const uploaded = version.uploadedAt instanceof Date ? version.uploadedAt.getTime() : 0;
  if (uploaded > 0) return uploaded;
  if (typeof version.versionIndex === 'number') return version.versionIndex;
  return 0;
}

function newestRecency(files: readonly VersionForStageGroup[]): number {
  return files.reduce((max, file) => Math.max(max, recency(file)), Number.NEGATIVE_INFINITY);
}

/**
 * Split a version list into stage groups and a single newest-first stream.
 * A stage is omitted until at least one version belongs to it.
 * Inside a group, newer files come first.
 * The stream places each group where its newest file belongs, so a new upload
 * stays at the top even when its stage would otherwise sort last.
 */
export function groupProjectFileVersionsByStage<T extends VersionForStageGroup>(
  versions: readonly T[],
): {
  unstaged: T[];
  stages: ProjectFileVersionStageGroup<T>[];
  stream: ProjectFileVersionStreamItem<T>[];
} {
  let newest: T | undefined;
  for (const version of versions) {
    if (!newest || recency(version) > recency(newest)) newest = version;
  }

  const byStage = new Map<ProjectFileDetailStage, T[]>();
  const unstaged: T[] = [];
  for (const version of versions) {
    const stage = version.detailStage;
    if (!stage) {
      unstaged.push(version);
      continue;
    }
    const list = byStage.get(stage);
    if (list) list.push(version);
    else byStage.set(stage, [version]);
  }

  const stages = PROJECT_FILE_DETAIL_STAGES.flatMap((stage) => {
    const files = byStage.get(stage);
    if (!files?.length) return [];
    const ordered = [...files].sort((a, b) => recency(b) - recency(a));
    return [{
      stage,
      label: PROJECT_FILE_DETAIL_STAGE_LABELS[stage],
      files: ordered,
      containsNewest: newest?.detailStage === stage,
    }];
  }).sort((a, b) => newestRecency(b.files) - newestRecency(a.files));

  const unstagedNewestFirst = [...unstaged].sort((a, b) => recency(b) - recency(a));
  const anchors: { at: number; item: ProjectFileVersionStreamItem<T> }[] = [
    ...unstagedNewestFirst.map((file) => ({ at: recency(file), item: { kind: 'file' as const, file } })),
    ...stages.map((group) => ({ at: newestRecency(group.files), item: { kind: 'stage' as const, group } })),
  ];
  anchors.sort((a, b) => b.at - a.at);

  return { unstaged: unstagedNewestFirst, stages, stream: anchors.map((anchor) => anchor.item) };
}
